import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SpecializedMap from './SpecializedMap';
import LocationSearchBar from './components/LocationSearchBar';
import AdminArticleModal from './components/AdminArticleModal';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { checkIsAdmin } from './services/mediaService';
import { getAllArticles, getAllArticlesAdmin } from './services/articleService';

// Simple ContactModal for Articles page
function ContactModal({ isOpen, onClose }) {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ background: 'white', padding: '2rem', borderRadius: 8, maxWidth: 400, width: '90%', position: 'relative', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        <h2>Contact Information</h2>
        <div className="contact-info" style={{ margin: '1.5rem 0' }}>
          <p style={{ fontSize: '1.1rem', margin: 0 }}><strong>Email:</strong> <a href="mailto:matt@golfnear.me">matt@golfnear.me</a></p>
        </div>
        <button onClick={onClose} className="close-button" style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#666' }}>Close</button>
      </div>
    </div>
  );
}

function Articles() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [golfCourses, setGolfCourses] = useState([]);
  const isMobile = window.innerWidth <= 768;
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Search bar state
  const [searchTerm, setSearchTerm] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  // Admin state
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);

  // Articles from Firestore
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);

  // Fetch golf courses data
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/courses.json`)
      .then((res) => res.json())
      .then((data) => {
        setGolfCourses(data);
      });
  }, []);

  // Auth state and admin check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        const adminStatus = await checkIsAdmin(firebaseUser.uid);
        setIsAdmin(adminStatus);
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch articles from Firestore
  const fetchArticles = async () => {
    setArticlesLoading(true);
    try {
      // Admin sees all articles (including drafts), public sees only published
      const fetchedArticles = isAdmin ? await getAllArticlesAdmin() : await getAllArticles();
      if (fetchedArticles && fetchedArticles.length > 0) {
        setArticles(fetchedArticles);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setArticlesLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [isAdmin]);

  // Build unique city/state pairs with course count
  const cityStateCounts = React.useMemo(() => {
    const map = new Map();
    golfCourses.forEach(c => {
      if (c.city && c.state) {
        const key = `${c.city}, ${c.state}`;
        map.set(key, (map.get(key) || 0) + 1);
      }
    });
    return Array.from(map.entries()).map(([cityState, count]) => ({ cityState, count }));
  }, [golfCourses]);

  // Combined search results with cities prioritized and ranked by popularity
  const filteredResults = React.useMemo(() => {
    if (!searchTerm.length) return [];

    const results = [];
    const searchTermLower = searchTerm.toLowerCase();
    
    // Normalize search term for Saint/St variations
    const normalizeText = (text) => {
      return text
        .replace(/\bsaint\b/gi, 'st')
        .replace(/\bst\./gi, 'st')
        .replace(/\bst\b/gi, 'saint');
    };
    
    const normalizedSearchTerm = normalizeText(searchTermLower);
    
    // First, add city/state matches with smart ranking
    const cityMatches = cityStateCounts
      .filter(({ cityState }) => {
        const normalizedCityState = normalizeText(cityState.toLowerCase());
        return normalizedCityState.includes(searchTermLower) || 
               normalizedCityState.includes(normalizedSearchTerm) ||
               cityState.toLowerCase().includes(searchTermLower);
      })
      .map(({ cityState, count }) => {
        // Calculate a score for ranking
        let score = count * 10; // Base score from course count
        
        // Bonus for exact city name match (e.g., "Dallas" matches "Dallas, Texas")
        const cityName = cityState.split(',')[0].toLowerCase();
        const normalizedCityName = normalizeText(cityName);
        
        if (cityName === searchTermLower || normalizedCityName === searchTermLower || normalizedCityName === normalizedSearchTerm) {
          score += 1000; // Big bonus for exact city name match
        }
        
        // Bonus for starting with search term
        if (cityName.startsWith(searchTermLower) || normalizedCityName.startsWith(searchTermLower) || normalizedCityName.startsWith(normalizedSearchTerm)) {
          score += 500;
        }
        
        // Bonus for containing search term (partial matches)
        if (cityName.includes(searchTermLower) || normalizedCityName.includes(searchTermLower) || normalizedCityName.includes(normalizedSearchTerm)) {
          score += 200;
        }
        
        // Bonus for state abbreviation match (e.g., "TX" in search)
        const stateName = cityState.split(',')[1]?.trim().toLowerCase();
        if (stateName && stateName.startsWith(searchTermLower)) {
          score += 300;
        }
        
        // Bonus for state abbreviation match (e.g., "Texas" matches "TX")
        const stateAbbreviations = {
          'alabama': 'al', 'alaska': 'ak', 'arizona': 'az', 'arkansas': 'ar', 'california': 'ca',
          'colorado': 'co', 'connecticut': 'ct', 'delaware': 'de', 'florida': 'fl', 'georgia': 'ga',
          'hawaii': 'hi', 'idaho': 'id', 'illinois': 'il', 'indiana': 'in', 'iowa': 'ia',
          'kansas': 'ks', 'kentucky': 'ky', 'louisiana': 'la', 'maine': 'me', 'maryland': 'md',
          'massachusetts': 'ma', 'michigan': 'mi', 'minnesota': 'mn', 'mississippi': 'ms', 'missouri': 'mo',
          'montana': 'mt', 'nebraska': 'ne', 'nevada': 'nv', 'new hampshire': 'nh', 'new jersey': 'nj',
          'new mexico': 'nm', 'new york': 'ny', 'north carolina': 'nc', 'north dakota': 'nd', 'ohio': 'oh',
          'oklahoma': 'ok', 'oregon': 'or', 'pennsylvania': 'pa', 'rhode island': 'ri', 'south carolina': 'sc',
          'south dakota': 'sd', 'tennessee': 'tn', 'texas': 'tx', 'utah': 'ut', 'vermont': 'vt',
          'virginia': 'va', 'washington': 'wa', 'west virginia': 'wv', 'wisconsin': 'wi', 'wyoming': 'wy'
        };
        
        if (stateName && stateAbbreviations[stateName] === searchTermLower) {
          score += 400; // Even bigger bonus for exact state abbreviation match
        }
        
        return { cityState, count, score };
      })
      .sort((a, b) => b.score - a.score) // Sort by score descending
      .slice(0, 8); // Limit city results to leave room for courses
    
    cityMatches.forEach(({ cityState, count }) => {
      results.push({
        type: 'city',
        cityState,
        count,
        label: cityState
      });
    });
    
    // Then add course matches
    const courseMatches = golfCourses.filter(course =>
      course.name.toLowerCase().includes(searchTermLower) ||
      course.club_name.toLowerCase().includes(searchTermLower)
    ).slice(0, 6); // Limit course results
    
    courseMatches.forEach(course => {
      results.push({
        type: 'course',
        courseId: course.course_id,
        name: course.name,
        clubName: course.club_name,
        city: course.city,
        state: course.state,
        lat: course.lat,
        lng: course.lng
      });
    });
    
    return results;
  }, [searchTerm, cityStateCounts, golfCourses]);

  // Handle search or selection
  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/map?search=${encodeURIComponent(searchTerm.trim())}`);
    }
  };
  
  const handleSelectResult = (result) => {
    if (result.type === 'city') {
      setSearchTerm(result.cityState);
      setShowCityDropdown(false);
      navigate(`/map?search=${encodeURIComponent(result.cityState)}`);
    } else if (result.type === 'course') {
      setSearchTerm(result.name);
      setShowCityDropdown(false);
      // Navigate to map with course coordinates
      navigate(`/map?lat=${result.lat}&lng=${result.lng}&course=${result.courseId}`);
    }
  };

  // Handle current location
  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        // Navigate to map with current location coordinates in the URL
        navigate(`/map?lat=${latitude}&lng=${longitude}`);
      },
      (error) => {
        console.error('Error getting current location:', error);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            alert('Location access was denied. Please enable location services in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Location information is unavailable.');
            break;
          case error.TIMEOUT:
            alert('Location request timed out.');
            break;
          default:
            alert('An unknown error occurred while getting your location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  const categories = [
    { id: 'all', name: 'All Articles' },
    { id: 'architects', name: 'Featured Architects' },
    { id: 'travel', name: 'Travel Guides' },
    { id: 'media', name: 'Golf Media' }
  ];

  // Helper to convert Firestore timestamp or string to Date
  const getDateValue = (date) => {
    if (!date) return new Date(0);
    if (date.toDate) return date.toDate(); // Firestore Timestamp
    if (date.seconds) return new Date(date.seconds * 1000); // Firestore Timestamp object
    return new Date(date); // String date
  };

  // Helper to format date for display
  const formatDate = (date) => {
    const d = getDateValue(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const filteredArticles = selectedCategory === 'all'
    ? articles
    : articles.filter(article => article.category === selectedCategory);

  // Sort articles by date (most recent first)
  const sortedArticles = [...filteredArticles].sort(
    (a, b) => getDateValue(b.date) - getDateValue(a.date)
  );

  // Show the most recent article as featured
  const featuredArticle = sortedArticles[0];
  const otherArticles = sortedArticles.slice(1);

  // Handle edit article
  const handleEditArticle = (article, e) => {
    e.stopPropagation();
    setEditingArticle(article);
    setIsArticleModalOpen(true);
  };

  // Handle create new article
  const handleCreateArticle = () => {
    setEditingArticle(null);
    setIsArticleModalOpen(true);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f7' }}>
      <Helmet>
        <title>Golf Near Me</title>
        <meta name="description" content="Find the best golf courses near you. Read articles about golf courses, equipment, tips, and news." />
      </Helmet>
      
      {/* Tall, Slim Header */}
      <div style={{
        width: isMobile ? '100%' : 1200,
        maxWidth: '100%',
        margin: '32px auto 0 auto',
        background: `url('/hero-bg.jpg') center/cover no-repeat`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '5rem 0' : '10rem 0',
        marginBottom: isMobile ? '1.5rem' : '2.5rem',
        borderRadius: 12,
        boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
      }}>
        {/* Search Section inside header */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: '100%', maxWidth: isMobile ? '95vw' : 650, margin: '0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: isMobile ? 28 : 40, color: '#fff', marginBottom: 18, textShadow: '0 2px 8px rgba(0,0,0,0.45)' }}>
              Where are you playing?
            </div>
            <div style={{ width: '100%' }}>
              <LocationSearchBar
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onSearch={handleSearch}
                filteredResults={filteredResults}
                onSelectResult={handleSelectResult}
                placeholder="Search by city, state, or course..."
                loading={golfCourses.length === 0}
                onFocus={() => setShowCityDropdown(true)}
                onBlur={() => setTimeout(() => setShowCityDropdown(false), 120)}
                showDropdown={showCityDropdown}
                onCurrentLocation={handleCurrentLocation}
              />
            </div>
            <div style={{ marginTop: 16, width: '100%' }}>
              <a
                href="#"
                onClick={e => { e.preventDefault(); navigate('/map'); }}
                style={{ color: '#fff', fontWeight: 600, fontSize: 16, textDecoration: 'underline', cursor: 'pointer', transition: 'color 0.2s', display: 'block', textShadow: '0 2px 8px rgba(0,0,0,0.45)' }}
                onMouseOver={e => e.currentTarget.style.color = '#e0e0e0'}
                onMouseOut={e => e.currentTarget.style.color = '#fff'}
              >
                Explore all courses
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Flex row for Back to Map and categories */}
      <div
        style={{
          maxWidth: 1200,
          margin: '2rem auto 0 auto',
          padding: isMobile ? '0 1rem' : '0 2rem',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'flex-start' : 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          gap: isMobile ? '1rem' : '1rem',
          marginBottom: '2.5rem'
        }}
      >
        <div style={{ display: 'flex', gap: '1rem', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`btn-filter ${selectedCategory === category.id ? 'active' : ''}`}
              style={{
                width: isMobile ? '100%' : 'auto',
                boxSizing: 'border-box'
              }}
            >
              {category.name}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/map')}
            className="btn btn-primary"
            style={{
              width: isMobile ? '100%' : 'auto',
              boxSizing: 'border-box'
            }}
          >
            View Course Map
          </button>
          {isAdmin && (
            <button
              onClick={handleCreateArticle}
              style={{
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                width: isMobile ? '100%' : 'auto',
                boxSizing: 'border-box'
              }}
            >
              + Create Article
            </button>
          )}
        </div>
      </div>

      {/* Featured Article */}
      {featuredArticle && (selectedCategory === 'all' || featuredArticle?.category === selectedCategory) ? (
        <div
          onClick={() => navigate(`/article/${featuredArticle.slug}`)}
          className="article-card-featured"
          style={{
            maxWidth: 1200,
            margin: '0 auto 2.5rem auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: isMobile ? 400 : 400
          }}
        >
          {/* Admin Edit Button */}
          {isAdmin && (
            <button
              onClick={(e) => handleEditArticle(featuredArticle, e)}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                zIndex: 10
              }}
            >
              Edit
            </button>
          )}
          {/* Draft Badge */}
          {isAdmin && !featuredArticle.published && (
            <div style={{
              position: 'absolute',
              top: 16,
              left: 16,
              background: '#f59e0b',
              color: 'white',
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              zIndex: 10
            }}>
              DRAFT
            </div>
          )}
          {/* Image on top */}
          <div style={{ width: '100%', height: isMobile ? 220 : 400, position: 'relative', background: '#f0f0f0' }}>
            <img
              src={featuredArticle.image}
              alt={featuredArticle.title}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
          {/* Text below image */}
          <div style={{ padding: isMobile ? '1.5rem' : '2.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 15, color: '#666', marginBottom: 8 }}>
              <span>{formatDate(featuredArticle.date)}</span>
              <span>{featuredArticle.readTime}</span>
            </div>
            <h2 style={{ margin: '0 0 1rem 0', fontSize: isMobile ? '1.3rem' : '2rem', color: '#222', lineHeight: 1.3 }}>{featuredArticle.title}</h2>
            <p style={{ color: '#444', fontSize: isMobile ? 15 : 17, lineHeight: 1.7, margin: 0 }}>{featuredArticle.excerpt}</p>
          </div>
        </div>
      ) : null}

      {/* Articles Grid */}
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: isMobile ? '0 1rem' : '0 2rem',
        paddingBottom: '4rem'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          {otherArticles.map(article => (
            <article
              key={article.id}
              onClick={() => navigate(`/article/${article.slug}`)}
              className="article-card"
            >
              {/* Admin Edit Button */}
              {isAdmin && (
                <button
                  onClick={(e) => handleEditArticle(article, e)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: '#7c3aed',
                    color: 'white',
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 600,
                    zIndex: 10
                  }}
                >
                  Edit
                </button>
              )}
              {/* Draft Badge */}
              {isAdmin && !article.published && (
                <div style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  background: '#f59e0b',
                  color: 'white',
                  padding: '3px 6px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 600,
                  zIndex: 10
                }}>
                  DRAFT
                </div>
              )}
              <div style={{
                position: 'relative',
                paddingBottom: '56.25%',
                background: '#f0f0f0'
              }}>
                <img
                  src={article.image}
                  alt={article.title}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
              <div style={{ padding: '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                  fontSize: 14,
                  color: '#666'
                }}>
                  <span>{formatDate(article.date)}</span>
                  <span>{article.readTime}</span>
                </div>
                <h2 style={{
                  margin: '0 0 0.5rem 0',
                  fontSize: '1.25rem',
                  color: '#333',
                  lineHeight: 1.4
                }}>
                  {article.title}
                </h2>
                <p style={{
                  margin: 0,
                  color: '#666',
                  fontSize: 14,
                  lineHeight: 1.6
                }}>
                  {article.excerpt}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Contact Us Link */}
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: isMobile ? '0 1rem' : '0 2rem',
        paddingBottom: '2rem',
        textAlign: 'center'
      }}>
        <button
          onClick={() => setIsContactModalOpen(true)}
          style={{
            background: 'none',
            border: 'none',
            color: '#1D665D',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: 16,
            padding: '0.5rem 1rem',
            transition: 'color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.color = '#1D665D'}
          onMouseOut={(e) => e.target.style.color = '#1D665D'}
        >
          Contact Us
        </button>
        <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
      </div>

      {/* Admin Article Modal */}
      <AdminArticleModal
        isOpen={isArticleModalOpen}
        onClose={() => {
          setIsArticleModalOpen(false);
          setEditingArticle(null);
        }}
        editingArticle={editingArticle}
        golfCourses={golfCourses}
        onArticleUpdated={fetchArticles}
      />
    </div>
  );
}

export default Articles; 